import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CoreContext } from 'context/CoreContext'
import { DoLogout } from 'services/authentication'
import { LoadEntryAreas, SelectEntryArea } from 'services/unifiedLogin'
import useI18n from 'hooks/useI18n'
import { trackEvent } from 'services/analytics'

export default function useController() {
  const { t } = useI18n()
  const history = useHistory()
  const { setUser } = useContext(CoreContext)
  const [areas, setAreas] = useState([])
  const [loading, setLoading] = useState(true)
  const [selecting, setSelecting] = useState(null)
  const [error, setError] = useState(false)
  const mounted = useRef(true)
  const busy = useRef(false)
  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const available = await LoadEntryAreas()
      if (!mounted.current) return
      if (available === null) { history.replace('/login'); return }
      setAreas(available)
      if (available.length === 1) {
        const destination = await SelectEntryArea(available[0])
        if (!mounted.current) return
        if (destination) history.replace(destination)
        else setError(true)
      }
    } catch { if (mounted.current) setError(true) }
    finally { if (mounted.current) setLoading(false) }
  }, [history])
  useEffect(() => { mounted.current = true; load(); return () => { mounted.current = false } }, [load])
  const select = async area => {
    if (busy.current) return
    busy.current = true
    setSelecting(area)
    setError(false)
    try {
      const destination = await SelectEntryArea(area)
      if (!mounted.current) return
      if (destination) { history.replace(destination); trackEvent('login', { method: 'email' }) }
      else { setAreas([]); setError(true) }
    } catch { if (mounted.current) { setAreas([]); setError(true) } }
    finally { busy.current = false; if (mounted.current) setSelecting(null) }
  }
  const logout = async () => {
    if (busy.current) return
    busy.current = true
    setSelecting('logout')
    await DoLogout()
    setUser(null)
    history.replace('/login')
  }
  return { t, areas, loading, selecting, error, select, retry: load, logout }
}
